import {
  cachedColorForCategory,
  cachedLabelForCategory,
} from '../planning/categoryCache';
import i18n, { getDateLocale } from '../../i18n';

export const START_HOUR = 0;
export const END_HOUR = 24;
export const HOUR_HEIGHT = 60;
export const DEFAULT_DURATION_MIN = 60;
export const DRAG_THRESHOLD_PX = 8;
export const MOBILE_DAY_COUNT = 3;
export const EVENT_GROUPS_KEY = 'followus.eventGroups';

export const EVENT_CATEGORIES = ['HO', 'HNO', 'Astreinte', 'Congés', 'Formation', 'Absence'];

export const CATEGORY_LABELS = {
  HO: 'Heures Ouvrées',
  HNO: 'Heures Non Ouvrées',
  Astreinte: 'Astreinte',
  Congés: 'Congés',
  Formation: 'Formation',
  Absence: 'Absence',
};

export const CATEGORY_COLORS = {
  HO: 'blue',
  HNO: 'primary',
  Astreinte: 'amber',
  Congés: 'green',
  Formation: 'yellow',
  Absence: 'rose',
};

const WEEKDAY_WEEKEND = [false, false, false, false, false, true, true];

function localeWeekdayMeta(mondayDate, dayOffset) {
  const d = addDays(mondayDate, dayOffset);
  return {
    label: d.toLocaleDateString(getDateLocale(), { weekday: 'long' }),
    short: d.toLocaleDateString(getDateLocale(), { weekday: 'short' }).toUpperCase().slice(0, 3),
    weekend: WEEKDAY_WEEKEND[dayOffset],
  };
}

function monthShort(date) {
  return date.toLocaleDateString(getDateLocale(), { month: 'short' });
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Local calendar date as YYYY-MM-DD. */
export function toIsoDateLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse YYYY-MM-DD as a local calendar date, or null if invalid. */
export function parseIsoDateLocal(iso) {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
    return null;
  }
  return startOfDay(date);
}

/** Monday 00:00 local for the week containing `date`. */
export function startOfWeekMonday(date = new Date()) {
  const d = startOfDay(date);
  const dow = d.getDay(); // 0=Sun … 6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + offset);
  return d;
}

export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function buildWeekDays(weekStart = startOfWeekMonday()) {
  const monday = startOfDay(weekStart);
  const today = startOfDay(new Date());
  return WEEKDAY_WEEKEND.map((_, i) => {
    const d = addDays(monday, i);
    const meta = localeWeekdayMeta(monday, i);
    return {
      day: i,
      label: meta.label,
      short: meta.short,
      date: `${monthShort(d)} ${d.getDate()}`,
      dayNum: d.getDate(),
      iso: toIsoDateLocal(d),
      weekend: meta.weekend,
      today: d.getTime() === today.getTime(),
      jsDate: d,
    };
  });
}

export function formatWeekRangeLabel(weekDays) {
  if (!weekDays?.length) return '';
  const start = weekDays[0];
  const end = weekDays[weekDays.length - 1];
  const startDate = start.jsDate || new Date(start.iso);
  const endDate = end.jsDate || new Date(end.iso);
  const year = endDate.getFullYear();
  if (startDate.getMonth() === endDate.getMonth()) {
    return `${monthShort(startDate)} ${startDate.getDate()} – ${endDate.getDate()}, ${year}`;
  }
  return `${monthShort(startDate)} ${startDate.getDate()} – ${monthShort(endDate)} ${endDate.getDate()}, ${year}`;
}

export function formatMobileRangeLabel(weekDays, mobileDayStart, count = MOBILE_DAY_COUNT) {
  const start = weekDays[mobileDayStart];
  const end = weekDays[Math.min(weekDays.length - 1, mobileDayStart + count - 1)];
  if (!start || !end) return formatWeekRangeLabel(weekDays);
  const startDate = start.jsDate || new Date(start.iso);
  const endDate = end.jsDate || new Date(end.iso);
  const year = endDate.getFullYear();
  if (startDate.getMonth() === endDate.getMonth()) {
    return `${monthShort(startDate)} ${startDate.getDate()} – ${endDate.getDate()}, ${year}`;
  }
  return `${monthShort(startDate)} ${startDate.getDate()} – ${monthShort(endDate)} ${endDate.getDate()}, ${year}`;
}

/** Initial range label for dashboard before the page mounts. */
export function getCurrentWeekLabel() {
  return formatWeekRangeLabel(buildWeekDays(startOfWeekMonday()));
}

/** @deprecated Use buildWeekDays(startOfWeekMonday()) — kept for any static imports. */
export const WEEK_DAYS = buildWeekDays(startOfWeekMonday());
export const FULL_WEEK_LABEL = formatWeekRangeLabel(WEEK_DAYS);

export const SEED_EVENTS = [
  { id: 'seed-1', day: 0, title: 'Morning Standup', start: 540, end: 600, category: 'HO' },
  { id: 'seed-2', day: 1, title: 'Lunch with Team', start: 720, end: 810, category: 'HNO' },
  { id: 'seed-3', day: 2, title: 'Quick Sync', start: 630, end: 690, category: 'Formation' },
  { id: 'seed-4', day: 2, title: 'Deep Work', start: 840, end: 1020, category: 'Astreinte' },
];

export function colorForCategory(category) {
  return cachedColorForCategory(category, CATEGORY_COLORS[category] || 'blue');
}

export function labelForCategory(category) {
  return cachedLabelForCategory(category, CATEGORY_LABELS[category] || category);
}

export function eventDisplayTitle(event) {
  const title = String(event?.title || '').trim();
  if (title) return title;
  return event?.category ? labelForCategory(event.category) : i18n.t('planning:untitledEvent');
}
